import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { routes } from 'types/routes'
import styles from './Auth.module.scss'
import SeoTags from 'components/seo-tags/SeoTags'
import useWindowSize from 'hooks/useWindowSize'
import LanguageSwitchBar from 'components/language-switch-bar/LanguageSwitchBar'
import { SMALL_DESKTOP_BREAKPOINT } from 'constants/viewport.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { isAuthenticated } from 'utils/localStorageService.ts'
import { UserTypes } from 'store/auth/types.ts'
import { USER_TYPE_CONFIG } from 'constants/userTypes.ts'
import { setUserType } from 'utils/userType.ts'
import { Flag } from '@boltenergy-be/design-system'
import { IS_BETA } from 'constants/envs.ts'

const Auth = () => {
  // i18n
  const { t } = useTranslation('auth')
  const language = useGetLanguage('lowercase')

  // React Router
  const { pathname } = useLocation()
  const navigate = useNavigate()

  // Window size
  const { isSmaller } = useWindowSize(SMALL_DESKTOP_BREAKPOINT)

  /**
   * Get page title
   * @returns {string}
   */
  const getPageTitle = (): string => {
    switch (pathname) {
      case routes.RESET_PASSWORD:
      case routes.REQUEST_NEW_PASSWORD:
      case routes.RESET_PASSWORD_SALES:
      case routes.REQUEST_NEW_PASSWORD_SALES:
        return t(($) => $.seo.reset)
      case routes.REGISTER:
        return t(($) => $.seo.register)
      default:
        return t(($) => $.seo.login)
    }
  }

  useEffect(() => {
    const authenticatedUserTypes = isAuthenticated()
    const routesByUserType: { [key in UserTypes]: (routes | string)[] } = {
      [UserTypes.CUSTOMER]: [USER_TYPE_CONFIG.customer.login, routes.REGISTER],
      [UserTypes.SUPER_USER]: [USER_TYPE_CONFIG.superuser.login],
      [UserTypes.SALES]: [USER_TYPE_CONFIG.sales.login]
    }

    const routesToMatch = [
      ...routesByUserType[UserTypes.CUSTOMER],
      ...routesByUserType[UserTypes.SUPER_USER],
      ...routesByUserType[UserTypes.SALES]
    ]

    if (routesToMatch.includes(pathname)) {
      for (const userType in routesByUserType) {
        // If the userType is already authenticated and the URL is the correct one, redirect to the correct page & set the userType
        if (authenticatedUserTypes[userType as UserTypes] && routesByUserType[userType as UserTypes].includes(pathname)) {
          setUserType(userType as UserTypes)
          void navigate(USER_TYPE_CONFIG[userType as UserTypes].home, { replace: true })
          break
        }
      }
    }
  }, [pathname])

  return (
    <>
      <SeoTags title={getPageTitle()} />

      <main className={styles['auth-wrapper']}>
        <section className={styles.auth}>
          <LanguageSwitchBar redirectUrl={`https://boltenergie.be/${language}`} />

          {IS_BETA && <Flag title={t(($) => $.betaFlag)} type="informative" className={styles.flag} />}

          <div className={styles['auth-content']}>
            <Outlet />
          </div>
        </section>

        {!isSmaller && (
          <aside className={styles.producer}>
            <img src="https://res.cloudinary.com/bolt-energie/image/upload/v1746715456/my-bolt/auth/login.jpg" alt="Lilian & Ruben" />
          </aside>
        )}
      </main>
    </>
  )
}

export default Auth
